import React, { useState } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { theme } from '../../../styles/theme';
import Page from '../../../common/Page';
import SubHeader from '../../../common/SubHeader';
import PrivateEventFields, { EMPTY_PRIVATE_FIELDS, fieldStyles } from '../../../common/PrivateEventFields';
import { createEvent } from '../../../common/api';
import { monthKeyOf } from '../../../common/dates';
import { eventDetailPath } from '../../../common/events';
import { useCurrentUser, isSuperAdmin } from '../../../common/UserContext';

const INITIAL = { eventType: 'public', eventName: '', when: '', where: '', notes: '', ...EMPTY_PRIVATE_FIELDS };

// Form di creazione evento. Pubblico: crea prodotto e payment link Stripe e link giftcard. Privato: solo scheda in Dynamo.
function CreateEvent() {
  const navigate = useNavigate();
  const user = useCurrentUser();
  const [form, setForm] = useState(INITIAL);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  const isPublic = form.eventType === 'public';

  const setField = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => (prev[field] ? { ...prev, [field]: undefined } : prev));
  };
  const update = (field) => (e) => setField(field, e.target.value);

  const validate = () => {
    const next = {};
    if (!form.eventName.trim()) next.eventName = 'Il nome evento è obbligatorio';
    if (!form.when) next.when = 'Data e ora sono obbligatorie';
    if (isPublic && !form.where.trim()) next.where = "L'indirizzo è obbligatorio per gli eventi pubblici";
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSaving(true);
    try {
      const payload = {
        eventType: form.eventType,
        eventName: form.eventName.trim(),
        when: form.when,
        where: form.where.trim(),
        notes: form.notes.trim(),
      };
      if (!isPublic) {
        Object.keys(EMPTY_PRIVATE_FIELDS).forEach((k) => { payload[k] = form[k]; });
      }
      const { event } = await createEvent(payload);
      toast.success('Evento creato');
      const back = `/admin/events/future?month=${monthKeyOf(event.when)}`;
      navigate(eventDetailPath(event, { back }));
    } catch (error) {
      console.error('Error creating event:', error);
      if (error.status === 409) {
        toast.error('Esiste già un evento con questo nome, data e ora');
      } else if (error.status === 400) {
        toast.error(error.message || 'Dati non validi');
      } else {
        toast.error("Errore nella creazione dell'evento, riprovare");
      }
    } finally {
      setSaving(false);
    }
  };

  if (!isSuperAdmin(user)) {
    return <Navigate to="/admin/dashboard" replace />;
  }

  return (
    <Page>
      <SubHeader title="Crea Evento" onBack={() => navigate('/admin/dashboard')} />
      <form style={styles.form} onSubmit={handleSubmit} autoComplete="off" noValidate>
        <label style={fieldStyles.label}>
          Tipologia di Evento *
          <select value={form.eventType} onChange={update('eventType')} style={fieldStyles.input}>
            <option value="public">Pubblico</option>
            <option value="private">Privato</option>
          </select>
        </label>

        <label style={fieldStyles.label}>
          Nome Evento *
          <input
            type="text"
            value={form.eventName}
            onChange={update('eventName')}
            placeholder={isPublic ? 'Ceramichine @Nome Location - Città' : 'es. Ceramichine @Ca’ Nova glamping - Mestrino'}
            style={fieldStyles.input}
          />
          {errors.eventName && <span style={fieldStyles.error}>{errors.eventName}</span>}
        </label>

        <label style={fieldStyles.label}>
          {isPublic ? 'Data e ora *' : 'Quando (data e ora di inizio) *'}
          <input type="datetime-local" value={form.when} onChange={update('when')} style={fieldStyles.input} />
          {errors.when && <span style={fieldStyles.error}>{errors.when}</span>}
        </label>

        {isPublic ? (
          <label style={fieldStyles.label}>
            Indirizzo *
            <input
              type="text"
              value={form.where}
              onChange={update('where')}
              placeholder="Via, numero, CAP Città Provincia"
              style={fieldStyles.input}
            />
            {errors.where && <span style={fieldStyles.error}>{errors.where}</span>}
          </label>
        ) : (
          <PrivateEventFields values={form} onChange={setField} />
        )}

        <label style={fieldStyles.label}>
          Note (facoltative)
          <textarea value={form.notes} onChange={update('notes')} rows={4} style={fieldStyles.textarea} />
        </label>

        <p style={styles.hint}>
          {isPublic
            ? 'Alla conferma vengono creati il prodotto e il link di pagamento Stripe (20 € a biglietto) e il link giftcard.'
            : 'Evento privato: nessuna vendita online, viene salvata solo la scheda con le informazioni. I campi oltre a nome e data sono facoltativi.'}
        </p>

        <button type="submit" style={saving ? styles.submitDisabled : styles.submit} disabled={saving}>
          {saving ? 'Creazione in corso...' : 'Crea Evento'}
        </button>
      </form>
    </Page>
  );
}

const styles = {
  form: {
    backgroundColor: theme.colors.white,
    borderRadius: theme.borderRadius,
    boxShadow: theme.shadows.main,
    padding: '20px',
    width: '100%',
    maxWidth: '500px',
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    gap: '14px',
  },
  hint: {
    fontSize: '12px',
    color: theme.colors.textSecondary,
    margin: 0,
  },
  submit: {
    padding: '12px 20px',
    backgroundColor: theme.colors.brand,
    color: theme.colors.white,
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '16px',
  },
  submitDisabled: {
    padding: '12px 20px',
    backgroundColor: theme.colors.border,
    color: theme.colors.white,
    border: 'none',
    borderRadius: '4px',
    cursor: 'not-allowed',
    fontSize: '16px',
  },
};

export default CreateEvent;
